// Pure helpers for the Notifications pages (spec 044 D39–D48): what a History row says, the
// New notification page's choices, and the server's name rule. No React, no MUI, no Firebase:
// Jest loads this file. Every "now" is a parameter so the tests can pin it.
import { toDate } from "./ticketPresentation";
import { whenLabel } from "./queuePresentation";
import { countText } from "./kitPresentation";

export const PAGE_SIZE = 10;

// One status word per document (044 D44, D45); the chip's MUI colour is a theme soft chip. Figma
// draws Scheduled (primary), Sent (success) and Draft (neutral); Sending and Failed take the
// info and error tones the same kit uses for a lesson in progress and one that failed.
export const STATUS = {
  draft: { label: "Draft", color: "default" },
  scheduled: { label: "Scheduled", color: "primary" },
  sending: { label: "Sending", color: "info" },
  sent: { label: "Sent", color: "success" },
  failed: { label: "Failed", color: "error" },
};
const STATUS_ORDER = Object.keys(STATUS);

// The toolbar's menus (ruling R5: Figma's "Status: Active" means nothing here).
export const STATUS_OPTIONS = [{ key: "all", label: "All" }, ...STATUS_ORDER.map((key) => ({ key, label: STATUS[key].label }))];
export const AUDIENCE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "student", label: "Students" },
  { key: "teacher", label: "Teachers" },
];

// Build ruling R4: every announcement is an in-app item and a push. Figma's pill says "In-app".
export const CHANNEL_LABEL = "In-app + push";

// The audience's age filter (044 D42; the list is ruling R1), as Figma labels it: an en dash with spaces.
export const AGE_RANGES = [
  { key: "all", label: "All ages", min: null, max: null },
  { key: "under7", label: "Under 7", min: 0, max: 6 },
  { key: "7-9", label: "7 – 9 years", min: 7, max: 9 },
  { key: "10-13", label: "10 – 13 years", min: 10, max: 13 },
  { key: "14-17", label: "14 – 17 years", min: 14, max: 17 },
  { key: "18plus", label: "18 and over", min: 18, max: 120 },
];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// "Just now" · "5 minutes ago" · "2 hours ago" · "Yesterday" · "3 days ago" · "2 weeks ago",
// and "In 10 minutes" · "In 18 hours" · "Tomorrow" · "In 3 days" · "In 2 weeks" ahead.
// Yesterday and Tomorrow are a day away by the clock (24–47 hours), not by the calendar, so
// the label needs no zone. "—" without a date.
export const relativeLabel = (value, now) => {
  const date = toDate(value);
  if (!date) return "—";
  const diff = date.getTime() - now.getTime();
  const ahead = diff > 0;
  const minutes = Math.floor(Math.abs(diff) / 60000);
  if (minutes < 1) return "Just now";
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const span =
    minutes < 60
      ? plural(minutes, "minute", "minutes")
      : hours < 24
      ? plural(hours, "hour", "hours")
      : days === 1
      ? null
      : days < 7
      ? plural(days, "day", "days")
      : plural(Math.floor(days / 7), "week", "weeks");
  if (span === null) return ahead ? "Tomorrow" : "Yesterday";
  return ahead ? `In ${span}` : `${span} ago`;
};

// The moment the When column is about (044 D40): a draft's last edit, a scheduled one's send
// time, a sending one's start, a sent or failed one's finish (or start, if it never finished).
export const whenAt = (a) => {
  switch (a.status) {
    case "draft":
      return toDate(a.updatedAt);
    case "scheduled":
      return toDate(a.sendAt);
    case "sending":
      return toDate(a.sendingAt) || toDate(a.sendAt);
    case "sent":
    case "failed":
      return toDate(a.sentAt) || toDate(a.sendingAt);
    default:
      return null;
  }
};

// The When cell's two lines. `opts.timeZone` pins the zone (tests); the reader's otherwise.
export const whenCell = (a, now, opts) => {
  const at = whenAt(a);
  switch (a.status) {
    case "draft":
      return ["Not scheduled", at ? `Edited ${relativeLabel(at, now)}` : ""];
    case "sending":
      return ["Sending now", at ? whenLabel(at, opts) : ""];
    case "scheduled":
    case "sent":
    case "failed":
      return [relativeLabel(at, now), at ? whenLabel(at, opts) : ""];
    default:
      return ["—", ""];
  }
};

// ["Students", "312 people"]: the server's count; for a draft of chosen people that was never
// counted, the people chosen. "—" when nothing is known.
export const audienceCell = (a) => {
  const audience = a.audience || {};
  const role = audience.role === "teacher" ? "Teachers" : "Students";
  let n = a.counts?.targeted;
  if (typeof n !== "number" && audience.kind === "specific" && Array.isArray(audience.uids)) n = audience.uids.length;
  return [role, typeof n === "number" ? `${countText(n)} ${n === 1 ? "person" : "people"}` : "—"];
};

// The line under the title: who last touched it, "Draft by …" while it is a draft.
export const senderLine = (a) => {
  const name = a.updatedByName || a.createdByName || "";
  if (a.status === "draft") return name ? `Draft by ${name}` : "Draft";
  return name || "—";
};

// The share of delivered items the app has marked read (044 D46), from announcementStats: "72.4%" for a sent
// announcement that reached someone; "—" otherwise, and while the number is still on its way.
export const openedRate = (a, opened) => {
  const delivered = a.counts?.delivered;
  if (a.status !== "sent" || !(delivered > 0) || typeof opened !== "number") return null;
  return (opened / delivered) * 100;
};
export const openedLabel = (a, opened) => {
  const rate = openedRate(a, opened);
  return rate === null ? "—" : `${rate.toFixed(1)}%`;
};

// The schedule step's three quick picks (044 D44), in the reader's local time: tomorrow at
// 09:00; the coming Friday at 18:00 (today, if it is a Friday morning); the Monday of next
// week at 08:00, never today.
export const quickPicks = (now) => {
  const at = (daysAhead, hour) => {
    const d = new Date(now);
    d.setDate(d.getDate() + daysAhead);
    d.setHours(hour, 0, 0, 0);
    return d;
  };
  let friday = at((5 - now.getDay() + 7) % 7, 18);
  if (friday <= now) friday = at(((5 - now.getDay() + 7) % 7) + 7, 18);
  return [
    { label: "Tomorrow, 09:00", at: at(1, 9) },
    { label: "Friday, 18:00", at: friday },
    { label: "Next Monday, 08:00", at: at((1 - now.getDay() + 7) % 7 || 7, 8) },
  ];
};

// The server's rule for `{name}` (044 D43, ruling R7): every `{name}` becomes the first name;
// with none it becomes nothing, a space left before , . ! ? goes, doubled spaces collapse.
export const fillName = (text, first) => {
  const name = String(first || "").trim();
  let out = String(text || "").replace(/\{name\}/g, name);
  if (!name) {
    out = out
      .replace(/[ \t]+([,.!?])/g, "$1") // "Hello !" → "Hello!"
      .replace(/,([.!?])/g, "$1") // "ready,!" → "ready!"
      .replace(/^[, \t]+/gm, ""); // "{name}, ready?" → "ready?", on any line
  }
  // A message's line breaks are its paragraphs: only runs of spaces collapse, line ends trim.
  return out.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/gm, "").trim();
};

// The toolbar's search and menus over the loaded rows (044 D40, ruling R5).
export const filterRows = (rows, { search = "", status = "all", role = "all" } = {}) => {
  const needle = search.trim().toLowerCase();
  return rows.filter(
    (a) =>
      (status === "all" || a.status === status) &&
      (role === "all" || a.audience?.role === role) &&
      (!needle || `${a.title || ""}\n${a.body || ""}`.toLowerCase().includes(needle))
  );
};

// The sortable heads (Figma draws sort on Notification, Audience, When, Status and Opened).
// A row with nothing to sort by sinks to the bottom whichever way the column sorts.
const SORT_VALUE = {
  title: (a) => (a.title || "").trim() || null,
  audience: (a) => audienceCell(a)[0],
  when: (a) => whenAt(a)?.getTime() ?? null,
  status: (a) => (STATUS_ORDER.includes(a.status) ? STATUS_ORDER.indexOf(a.status) : null),
  opened: (a, opened) => openedRate(a, opened?.[a.id]),
};

export const sortRows = (rows, key, dir = "asc", { opened } = {}) => {
  const value = SORT_VALUE[key];
  if (!value) return rows;
  const sign = dir === "desc" ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = value(x, opened);
    const b = value(y, opened);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return sign * (typeof a === "string" ? a.localeCompare(b, "en", { sensitivity: "base" }) : a - b);
  });
};

// The table's empty row: which of the search and the menus hides everything.
export const emptyState = ({ search = "", status = "all", role = "all" } = {}) => {
  if (search.trim()) return { title: `No notification matches "${search.trim()}".`, message: "Search looks in titles and messages.", clear: "search" };
  if (status !== "all" || role !== "all") return { title: "No notifications match these filters.", message: "Clear them to see every notification.", clear: "filters" };
  return { title: "No notifications yet", message: "Announcements you send, schedule or draft appear here for 90 days.", clear: null };
};
